/**
 * Floor footprint as an ordered vertex ring (plan pixels) + expansion merge.
 */

const EPS = 1e-3;

/** @typedef {{ id: string, px: number, py: number }} FloorVertex */
/** @typedef {{ edgeIndex: number, px: number, py: number, t: number }} FloorBoundaryPick */

const LEGACY_ORDER = ["nw", "ne", "se", "sw"];

function signedArea(verts) {
  let a = 0;
  for (let i = 0; i < verts.length; i++) {
    const p = verts[i];
    const q = verts[(i + 1) % verts.length];
    a += p.px * q.py - q.px * p.py;
  }
  return a / 2;
}

function distToSegment(p, a, b) {
  const dx = b.px - a.px;
  const dy = b.py - a.py;
  const len2 = dx * dx + dy * dy;
  if (len2 < 1e-9) return { dist: Math.hypot(p.px - a.px, p.py - a.py), t: 0 };
  let t = ((p.px - a.px) * dx + (p.py - a.py) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  const qx = a.px + t * dx;
  const qy = a.py + t * dy;
  return { dist: Math.hypot(p.px - qx, p.py - qy), t, px: qx, py: qy };
}

/** Migrate legacy four-corner floor to an ordered vertex ring. */
export function ensureFloorVertices(floor) {
  if (!floor) return [];
  if (Array.isArray(floor.vertices) && floor.vertices.length >= 3) {
    return floor.vertices;
  }
  const c = floor.corners;
  const valid = (p) => p && Number.isFinite(Number(p.px)) && Number.isFinite(Number(p.py));
  if (valid(c?.nw) && valid(c.ne) && valid(c.se) && valid(c.sw)) {
    floor.vertices = LEGACY_ORDER.map((id) => ({
      id,
      px: Number(c[id].px),
      py: Number(c[id].py),
    }));
    return floor.vertices;
  }
  const x0 = Math.min(Number(floor.x0) || 0, Number(floor.x1) || 0);
  const y0 = Math.min(Number(floor.y0) || 0, Number(floor.y1) || 0);
  const x1 = Math.max(Number(floor.x0) || 0, Number(floor.x1) || 0);
  const y1 = Math.max(Number(floor.y0) || 0, Number(floor.y1) || 0);
  floor.vertices = [
    { id: "nw", px: x0, py: y0 },
    { id: "ne", px: x1, py: y0 },
    { id: "se", px: x1, py: y1 },
    { id: "sw", px: x0, py: y1 },
  ];
  floor.corners = {
    nw: { px: x0, py: y0 },
    ne: { px: x1, py: y0 },
    se: { px: x1, py: y1 },
    sw: { px: x0, py: y1 },
  };
  return floor.vertices;
}

/** CCW vertex ring (mutates winding if needed). */
export function floorVertexRing(floor) {
  const verts = ensureFloorVertices(floor).map((v) => ({
    id: String(v.id),
    px: Number(v.px),
    py: Number(v.py),
  }));
  if (verts.length >= 3 && signedArea(verts) < 0) verts.reverse();
  return verts;
}

export function syncFloorLegacyFields(floor) {
  if (!floor) return;
  const verts = simplifyFloorRing(floorVertexRing(floor));
  floor.vertices = verts;
  if (!Number.isFinite(floor.nextVertex)) floor.nextVertex = verts.length;
  const xs = verts.map((v) => v.px);
  const ys = verts.map((v) => v.py);
  floor.x0 = Math.min(...xs);
  floor.y0 = Math.min(...ys);
  floor.x1 = Math.max(...xs);
  floor.y1 = Math.max(...ys);
  const byId = Object.fromEntries(verts.map((v) => [v.id, v]));
  floor.corners = {
    nw: byId.nw ?? { px: floor.x0, py: floor.y0 },
    ne: byId.ne ?? { px: floor.x1, py: floor.y0 },
    se: byId.se ?? { px: floor.x1, py: floor.y1 },
    sw: byId.sw ?? { px: floor.x0, py: floor.y1 },
  };
}

function pickFloorBoundaryWithin(floor, p, maxDist) {
  const verts = floorVertexRing(floor);
  if (verts.length < 3) return null;

  let best = null;
  let bestD = maxDist;

  for (let i = 0; i < verts.length; i++) {
    const v = verts[i];
    const d = Math.hypot(p.px - v.px, p.py - v.py);
    if (d < bestD) {
      bestD = d;
      best = { kind: "vertex", edgeIndex: i, px: v.px, py: v.py, t: 0, vertexIndex: i };
    }
  }

  for (let i = 0; i < verts.length; i++) {
    const a = verts[i];
    const b = verts[(i + 1) % verts.length];
    const hit = distToSegment(p, a, b);
    if (hit.dist < bestD) {
      bestD = hit.dist;
      best = { kind: "edge", edgeIndex: i, px: hit.px, py: hit.py, t: hit.t };
    }
  }

  return best;
}

/** Pick nearest floor corner or edge point within snapPx. */
export function pickFloorBoundary(floor, p, snapPx) {
  return pickFloorBoundaryWithin(floor, p, snapPx);
}

/** Nearest point on the floor outline — no distance limit (for expand start anywhere). */
export function nearestFloorBoundary(floor, p) {
  return pickFloorBoundaryWithin(floor, p, Infinity);
}

function arcIndices(n, from, to, forward) {
  const out = [];
  let i = from;
  out.push(i);
  if (from === to) return out;
  while (true) {
    i = forward ? (i + 1) % n : (i - 1 + n) % n;
    out.push(i);
    if (i === to) break;
    if (out.length > n + 1) break;
  }
  return out;
}

function splitAnchor(verts, anchor, idGen) {
  const n = verts.length;
  const i = ((anchor.edgeIndex % n) + n) % n;
  const a = verts[i];
  const b = verts[(i + 1) % n];
  if (Math.hypot(anchor.px - a.px, anchor.py - a.py) < EPS) {
    return { verts: [...verts], vertexIndex: i };
  }
  if (Math.hypot(anchor.px - b.px, anchor.py - b.py) < EPS) {
    return { verts: [...verts], vertexIndex: (i + 1) % n };
  }
  const pt = { id: idGen(), px: anchor.px, py: anchor.py };
  const next = [...verts];
  next.splice(i + 1, 0, pt);
  return { verts: next, vertexIndex: i + 1 };
}

function locateAnchor(verts, anchor) {
  const n = verts.length;
  for (let i = 0; i < n; i++) {
    if (Math.hypot(verts[i].px - anchor.px, verts[i].py - anchor.py) < EPS) {
      return { edgeIndex: i, px: verts[i].px, py: verts[i].py, t: 0, kind: "vertex" };
    }
  }
  let best = { edgeIndex: 0, px: anchor.px, py: anchor.py, t: 0.5, kind: "edge" };
  let bestD = Infinity;
  for (let i = 0; i < n; i++) {
    const hit = distToSegment({ px: anchor.px, py: anchor.py }, verts[i], verts[(i + 1) % n]);
    if (hit.dist < bestD) {
      bestD = hit.dist;
      best = { edgeIndex: i, px: hit.px, py: hit.py, t: hit.t, kind: "edge" };
    }
  }
  return best;
}

function buildMergedRing(ring, startIdx, endIdx, chainVerts, replaceForwardArc) {
  const n = ring.length;
  const fwd = arcIndices(n, startIdx, endIdx, true);
  const back = arcIndices(n, startIdx, endIdx, false);
  const kept = replaceForwardArc ? back : fwd;

  const out = [ring[startIdx], ...chainVerts, ring[endIdx]];
  for (let k = kept.length - 2; k >= 1; k--) {
    out.push(ring[kept[k]]);
  }

  const deduped = [];
  for (const v of out) {
    const prev = deduped[deduped.length - 1];
    if (prev && Math.hypot(prev.px - v.px, prev.py - v.py) < EPS) continue;
    deduped.push(v);
  }
  if (deduped.length < 3) return null;
  if (signedArea(deduped) < 0) deduped.reverse();
  return deduped;
}

function pointInRingPx(point, verts) {
  if (!point || !verts?.length) return false;
  const x = point.px;
  const y = point.py;
  let inside = false;
  for (let i = 0, j = verts.length - 1; i < verts.length; j = i++) {
    const xi = verts[i].px;
    const yi = verts[i].py;
    const xj = verts[j].px;
    const yj = verts[j].py;
    const hit = (yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi + 1e-12) + xi;
    if (hit) inside = !inside;
  }
  return inside;
}

/** True when at least one chain point sits outside the original floor ring (outward expand). */
function chainBulgesOutside(ring, chainVerts) {
  if (!chainVerts?.length) return true;
  return chainVerts.some((p) => !pointInRingPx(p, ring));
}

function findVertexIndex(verts, px, py) {
  for (let i = 0; i < verts.length; i++) {
    if (Math.hypot(verts[i].px - px, verts[i].py - py) < EPS) return i;
  }
  return -1;
}

function segmentsProperIntersect(a, b, c, d) {
  const det = (b.px - a.px) * (d.py - c.py) - (b.py - a.py) * (d.px - c.px);
  if (Math.abs(det) < 1e-9) return false;
  const t = ((c.px - a.px) * (d.py - c.py) - (c.py - a.py) * (d.px - c.px)) / det;
  const u = ((c.px - a.px) * (b.py - a.py) - (c.py - a.py) * (b.px - a.px)) / det;
  return t > 1e-6 && t < 1 - 1e-6 && u > 1e-6 && u < 1 - 1e-6;
}

/** True when non-adjacent edges of a ring cross (invalid floor footprint). */
export function polygonSelfIntersects(verts) {
  const n = verts?.length ?? 0;
  if (n < 4) return false;
  for (let i = 0; i < n; i++) {
    const a = verts[i];
    const b = verts[(i + 1) % n];
    for (let j = i + 1; j < n; j++) {
      if (j === i) continue;
      if (j === (i + 1) % n || (j + 1) % n === i) continue;
      if (i === 0 && j === n - 1) continue;
      const c = verts[j];
      const d = verts[(j + 1) % n];
      if (segmentsProperIntersect(a, b, c, d)) return true;
    }
  }
  return false;
}

function nearlyCollinear(a, b, c, eps = EPS) {
  const cross = (b.px - a.px) * (c.py - b.py) - (b.py - a.py) * (c.px - b.px);
  const span = Math.hypot(c.px - a.px, c.py - a.py);
  return Math.abs(cross) <= eps * Math.max(1, span);
}

/**
 * Remove duplicate and collinear vertices so expanded floors stay one clean ring.
 * @param {FloorVertex[]} verts
 */
export function simplifyFloorRing(verts, eps = EPS) {
  if (!Array.isArray(verts) || verts.length < 3) return verts ?? [];

  const deduped = [];
  for (const v of verts) {
    const prev = deduped[deduped.length - 1];
    if (prev && Math.hypot(prev.px - v.px, prev.py - v.py) < eps) continue;
    deduped.push({ id: String(v.id), px: Number(v.px), py: Number(v.py) });
  }
  if (deduped.length >= 2) {
    const first = deduped[0];
    const last = deduped[deduped.length - 1];
    if (Math.hypot(first.px - last.px, first.py - last.py) < eps) deduped.pop();
  }

  let ring = deduped;
  let changed = true;
  while (changed && ring.length > 3) {
    changed = false;
    for (let i = 0; i < ring.length; i++) {
      const prev = ring[(i - 1 + ring.length) % ring.length];
      const cur = ring[i];
      const next = ring[(i + 1) % ring.length];
      if (nearlyCollinear(prev, cur, next, eps)) {
        ring = [...ring.slice(0, i), ...ring.slice(i + 1)];
        changed = true;
        break;
      }
    }
  }

  if (ring.length >= 3 && signedArea(ring) < 0) ring.reverse();
  return ring.length >= 3 ? ring : deduped;
}

function anchorsNear(a, b, snap) {
  if (!a || !b) return false;
  return Math.hypot(a.px - b.px, a.py - b.py) < snap;
}

/**
 * Merge an outward chain back into the floor ring.
 * @param {FloorVertex[]} verts
 * @param {FloorBoundaryPick} startAnchor
 * @param {{ px: number, py: number }[]} chain outward points (excluding anchors)
 * @param {FloorBoundaryPick} endAnchor
 * @param {() => string} idGen
 */
export function mergeFloorExpansion(verts, startAnchor, chain, endAnchor, idGen) {
  if (!verts?.length || verts.length < 3 || !startAnchor || !endAnchor) return null;
  if (!Array.isArray(chain)) chain = [];

  let ring = floorVertexRing({ vertices: verts });
  let start = splitAnchor(ring, startAnchor, idGen);
  ring = start.verts;

  const endRel = locateAnchor(ring, endAnchor);
  let end = splitAnchor(ring, endRel, idGen);
  ring = end.verts;

  const startIdx = findVertexIndex(ring, startAnchor.px, startAnchor.py);
  const endIdx = findVertexIndex(ring, endRel.px, endRel.py);
  if (startIdx < 0 || endIdx < 0) return null;

  if (startIdx === endIdx) return null;

  const chainVerts = [];
  for (const p of chain) {
    const pt = { id: idGen(), px: p.px, py: p.py };
    const last = chainVerts[chainVerts.length - 1];
    if (last && Math.hypot(last.px - pt.px, last.py - pt.py) < EPS) continue;
    chainVerts.push(pt);
  }
  const origArea = Math.abs(signedArea(ring));

  const candForward = buildMergedRing(ring, startIdx, endIdx, chainVerts, true);
  const candBackward = buildMergedRing(ring, startIdx, endIdx, chainVerts, false);

  const scoreCandidate = (candidate) => {
    if (!candidate || candidate.length < 3) return null;
    const cleaned = simplifyFloorRing(candidate);
    if (cleaned.length < 3 || polygonSelfIntersects(cleaned)) return null;
    if (!chainBulgesOutside(ring, chainVerts)) return null;
    const area = Math.abs(signedArea(cleaned));
    if (area <= origArea + EPS) return null;
    return { area, cleaned };
  };

  const pickF = scoreCandidate(candForward);
  const pickB = scoreCandidate(candBackward);
  if (!pickF && !pickB) return null;
  if (!pickF) return pickB.cleaned;
  if (!pickB) return pickF.cleaned;
  return pickB.area > pickF.area ? pickB.cleaned : pickF.cleaned;
}

export { anchorsNear };

export default {
  ensureFloorVertices,
  floorVertexRing,
  syncFloorLegacyFields,
  simplifyFloorRing,
  polygonSelfIntersects,
  pickFloorBoundary,
  nearestFloorBoundary,
  mergeFloorExpansion,
  anchorsNear,
};
